<template>
  <template v-if="!loading">
    <form
      autocomplete="off"
      class="gap-0 gap-y-4 max-w-xl"
      @submit.prevent="submitForm"
    >
      <div class="col-span-12">
        <TextInput
          v-model="formData.name"
          label="Event Name"
          type="text"
          required
          class="rounded-sm mb-8"
          :validation-error="errors && 'name' in errors && errors.name[0]"
        />

        <DatePicker
          v-model="formData.date"
          timezone="Africa/Johannesburg"
          format="dd MMM yyyy"
          :flow="['calendar']"
          model-type="iso"
          :hide-navigation="['month', 'year', 'calendar', 'time', 'minutes', 'hours', 'seconds']"
          :auto-apply="true"
          label="Event Date"
          required
          class="rounded-sm mb-8"
          :validation-error="errors && 'date' in errors && errors.date[0]"
        />

        <TextInput
          v-model="formData.location"
          label="Event Location"
          type="text"
          required
          class="rounded-sm mb-8"
          :validation-error="errors && 'location' in errors && errors.location[0]"
        />

        <template v-if="!event?.id">
          <div class="mb-8">
            <label class="cursor-pointer flex items-center gap-2">
              <input
                v-model="formData.sponsored"
                type="checkbox"
                :checked="formData.sponsored"
                class="!cursor-pointer"
              >
              Sponsored
            </label>
          </div>
        </template>

        <div
          v-if="devices.length > 0"
          class="flex flex-col px-3 py-4 bg-secondary-subtle rounded gap-4"
        >
          <template
            v-for="device in devices"
            :key="device.id"
          >
            <div class="flex item-center gap-2 cursor-pointer w-fit">
              <v-tooltip :delay="{ show: 500 }">
                <label class="cursor-pointer flex items-center gap-3">
                  <input
                    type="checkbox"
                    :checked="formData.devices.includes(device.id)"
                    :value="device.id"
                    @change="toggleDevice"
                  >
                  {{ device.name }} : {{ device?.event?.name }}
                </label>

                <template #popper>
                  <span class="!text-sm">
                    Selecting this device will remove it from its previous event
                  </span>
                </template>
              </v-tooltip>
            </div>
          </template>
        </div>

        <div class="mt-5">
          <label class="cursor-pointer flex items-center gap-2">
            <input
              v-model="formData.canPurchaseBulkPhotos"
              type="checkbox"
              :checked="formData.canPurchaseBulkPhotos"
              class="!cursor-pointer"
            >
            Enable photo packs
          </label>
        </div>

        <div class="flex justify-end gap-2 mt-5">
          <AppLink
            :to="{ name: 'app_admin_event_list'}"
          >
            <AppButton
              :disabled="isSubmitting"
              type="button"
              class="btn btn-outline btn-secondary px-9 rounded-sm"
            >
              Cancel
            </AppButton>
          </AppLink>

          <AppButton
            :processing="isSubmitting"
            class="btn-solid btn-secondary px-9 rounded-sm w-32"
          >
            {{ isSubmitting ? 'Saving' : 'Save' }}
          </AppButton>
        </div>
      </div>
    </form>
  </template>

  <template v-else>
    <AppSpinner
      v-if="loading"
      class="page-spinner"
    />
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { AppButton, AppLink, AppSpinner } from '@/components/app';
import { DatePicker, TextInput } from '@/components/forms/inputs';

import { useFormSubmit, useHttp } from '@/composables';

const route = useRoute();
const router = useRouter();

const loading = ref(true);

const devices = ref([]);

const props = defineProps({
  event: {
    type: Object,
    default: null,
  },
});

const formData = reactive({
  name: props.event?.name || null,
  date: props.event?.date || null,
  location: props.event?.location || null,
  devices: [],
  canPurchaseBulkPhotos: props.event?.canPurchaseBulkPhotos || null,
});

if (!props.event) {
  formData.sponsored = false;
  formData.canPurchaseBulkPhotos = true;
}

const { isSubmitting, errors } = useFormSubmit();

onMounted(async () => {
  await loadDevices();

  if (devices.value) {
    formData.devices = devices.value.filter(device => device.event && device.event.id === props.event?.id).map(device => device.id);
  }
});

const submitForm = async () => {
  isSubmitting.value = true;
  let response;

  if (props.event) {
    response = await useHttp().put(`/events/${route.params.slug}`, formData);
  } else {
    response = await useHttp().post('/events', formData);
  }

  if (response.statusCode === HttpStatusCode.Ok) {
    await router.push({ name: 'app_admin_event_list' });
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors;
  }

  isSubmitting.value = false;
};

const loadDevices = async () => {
  loading.value = true;

  const response = await useHttp().get('/devices');

  if (response.statusCode === HttpStatusCode.Ok) {
    devices.value = response.data.items;
  }

  loading.value = false;
};

const toggleDevice = event => {
  const deviceId = parseInt(event.target.value);
  const checked = event.target.checked;

  if (checked && !formData.devices.includes(deviceId)) {
    formData.devices.push(deviceId);
  } else if (formData.devices.includes(deviceId)) {
    formData.devices = formData.devices.filter(id => deviceId !== id);
  }
};

</script>
